import {defineMessages, FormattedMessage} from 'react-intl';
import {useCallback, useEffect, useState} from 'react';
import {customers, cancelCircle} from '@sail/icons/react/Icon';
import {Button, Icon, Spinner} from '@sail/ui';
import {useWidget} from '../useWidget';

const messages = defineMessages({
  shareScreenButton: {
    id: 'conversations_ui.widget.liveChatTopBar.shareScreenButton',
    description: 'Button to share screen',
    defaultMessage: 'Share screen',
  },
  waitingForAgent: {
    id: 'conversations_ui.widget.liveChatTopBar.waitingForAgent',
    description: 'Button text when waiting for agent',
    defaultMessage: 'Waiting for agent',
  },
  stopSharing: {
    id: 'conversations_ui.widget.liveChatTopBar.stopSharing',
    description: 'Button text when stopping screenshare',
    defaultMessage: 'Stop sharing',
  },
});

type Props = {
  channel?: 'chat' | 'rac';
  agentNames?: string[] | null;
};

export const ScreenshareButton = ({channel, agentNames}: Props) => {
  const widget = useWidget();
  const [screenshareStatus, setScreenshareStatus] =
    useState<string>('INACTIVE');

  const checkNewScreenshareStatus = useCallback(async () => {
    try {
      const response = await widget.requestAction(
        {
          name: 'get-screenshare-status',
          data: {},
        },
        {waitForResponse: true},
      );

      if (
        response.data &&
        typeof response.data === 'object' &&
        'state' in response.data
      ) {
        setScreenshareStatus(response.data.state as string);
      } else {
        setScreenshareStatus('INACTIVE');
      }
    } catch (error) {
      setScreenshareStatus('INACTIVE');
    }
  }, [widget]);

  useEffect(() => {
    checkNewScreenshareStatus();
  }, [checkNewScreenshareStatus]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      checkNewScreenshareStatus();
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkNewScreenshareStatus]);

  useEffect(() => {
    if (agentNames && agentNames.length > 0) {
      widget.requestAction(
        {
          name: 'set-screenshare-name',
          data: {
            agentName: agentNames[0],
            channel,
          },
        },
        {waitForResponse: true},
      );
    }
  }, [agentNames, widget, channel]);

  const onNavigateToScreenshare = useCallback(() => {
    widget.requestAction(
      {
        name: 'request-screenshare',
        data: {},
      },
      {
        waitForResponse: false,
      },
    );
  }, [widget]);

  const onStopScreenshare = useCallback(() => {
    widget.requestAction(
      {
        name: 'stop-screenshare',
        data: {},
      },
      {waitForResponse: false},
    );
  }, [widget]);

  if (screenshareStatus === 'WAITING_FOR_AGENT') {
    return (
      <Button
        size="small"
        onPress={() => {
          onStopScreenshare();
        }}
        css={{flex: 1, gap: 'small', height: '28px'}}
      >
        <Spinner />
        <FormattedMessage {...messages.waitingForAgent} />
      </Button>
    );
  }

  if (screenshareStatus === 'ACTIVE') {
    return (
      <>
        <Button
          size="small"
          onPress={() => {
            onStopScreenshare();
          }}
          type="destructive"
          data-testid="conversations-widget-share-screen-button"
          css={{flex: 1, gap: 'small', height: '28px'}}
        >
          <Icon icon={cancelCircle} />
          <FormattedMessage {...messages.stopSharing} />
        </Button>
      </>
    );
  }

  const isDisabled = !agentNames || agentNames.length === 0;

  return (
    <Button
      size="small"
      onPress={() => {
        onNavigateToScreenshare();
      }}
      data-testid="conversations-widget-share-screen-button"
      css={{flex: 1, gap: 'small', height: '28px'}}
      disabled={isDisabled}
    >
      <Icon icon={customers} />
      <FormattedMessage {...messages.shareScreenButton} />
    </Button>
  );
};
